import {
  Box,
  HStack,
  KdsButton,
  KdsText,
  VStack,
} from "@kazaar-platform/design-system";
import { RiDownload2Line } from "@remixicon/react";
import { useTranslate } from "@tolgee/react";
import { WorkflowPostponedActivityReadDtoActivityEnum } from "@/config/apiClient";
import { uploadMultipleFiles } from "@/helpers/files";
import { useOrderItems, useToasts } from "@/hooks";
import { type GraphismBlocProps } from "./GraphismBloc.types";
import { GraphismVersionBloc } from "./GraphismVersionBloc";

export const GraphismBloc = ({
  orderItem,
  onTabNavigation,
}: GraphismBlocProps) => {
  const { t } = useTranslate();
  const { add } = useToasts();

  const { useDownloadAllOrderItemHDFiles } = useOrderItems();
  const {
    refetch: fetchAllHDFiles,
    isLoading: isDownloadingAllFiles,
    isFetching: isFetchingDownload,
  } = useDownloadAllOrderItemHDFiles({
    orderId: orderItem?.orderId,
    orderItemId: orderItem?.id,
  });

  const sandboxItem = orderItem?.sandboxItem;
  const sandboxItemVersions = sandboxItem?.SandboxItemVersion;
  const hasSomeHDFiles = sandboxItemVersions?.some((version) => !!version.file);

  const hasAModificationRequest = orderItem?.Workflow.PostponedActivities.some(
    (postponedActivity) =>
      postponedActivity.activity ===
      WorkflowPostponedActivityReadDtoActivityEnum.DecideOnModificationRequest
  );

  const handleUploadMultipleHDFiles = async () => {
    try {
      const { data, error } = await fetchAllHDFiles();

      if (error || !data?.data) {
        return;
      }

      void uploadMultipleFiles(
        data?.data as Blob, // TOREFACTO : Ensure better typing from backend
        orderItem?.sandboxItem?.catalogMedium?.name ||
          orderItem?.sandboxItem?.name
      );
    } catch {
      add({
        state: "error",
        title: t("buttons.errorDownloadAllFiles"),
      });
    }
  };

  if (!sandboxItemVersions) {
    return null;
  }

  return (
    <Box w="100%" rounded="br-md" bgColor="elevation.first" p="sp-md">
      <VStack gap="sp-md">
        <HStack gap="sp-sm" justify="space-between">
          <KdsText size="title-sm">
            {t("order.myOrdersItemDetails.design")}
          </KdsText>

          {hasSomeHDFiles && !orderItem.sandboxItem.withPreMedia && (
            <KdsButton
              variant="outlined"
              color="neutral"
              onPress={handleUploadMultipleHDFiles}
              iconLeft={RiDownload2Line}
              isLoading={isDownloadingAllFiles || isFetchingDownload}
            >
              {t("buttons.downloadAllFiles")}
            </KdsButton>
          )}
        </HStack>

        {sandboxItemVersions.map((version, index) => {
          const hdFile = sandboxItem.withPreMedia
            ? sandboxItem.preMediaPropositionsFiles?.[0]
            : version.file;

          return (
            <GraphismVersionBloc
              hdFile={hdFile}
              hasAModificationRequest={hasAModificationRequest}
              key={`${version.id}-${version.name}`}
              index={index}
              orderItem={orderItem}
              version={version}
              onTabNavigation={onTabNavigation}
            />
          );
        })}
      </VStack>
    </Box>
  );
};
